import { APPLE_MINIMUM_VERSION } from '#/store/services/BrowserGuard/Logic/browserMinimumVersion'
import {
  formatMinimumVersionForDisplay,
  formatVersionForDisplay,
  meetsMinimumVersion,
} from '#/store/services/BrowserGuard/Logic/browserVersion'
import { BrowserValidationResult } from '#/store/services/BrowserGuard/types'

type AppleMobileOS = 'iOS' | 'iPadOS'

type ApplePlatformParams = {
  osName?: string
  browserName?: string
  browserVersion?: string
  osVersion?: string
}

function isAppleMobileOS(osName?: string): osName is AppleMobileOS {
  return osName === 'iOS' || osName === 'iPadOS'
}

/**
 * Retorna a versão da plataforma Apple que deve ser usada na validação.
 *
 * No iOS/iPadOS, a versão do sistema determina a versão do WebKit
 * disponível para todos os navegadores.
 *
 * No macOS, somente o Safari é validado pela versão do WebKit.
 * Outros navegadores utilizam sua própria versão de engine — nesse caso
 * a versão do Safari é a própria versão do navegador detectada pelo Bowser.
 */
export function getAppleVersion({
  browserName,
  browserVersion,
  osName,
  osVersion,
}: ApplePlatformParams): string | undefined {
  if (isAppleMobileOS(osName)) {
    return osVersion
  }

  if (osName === 'macOS' && browserName === 'Safari') {
    return browserVersion
  }

  return undefined
}

/**
 * Valida a compatibilidade do WebKit em iOS/iPadOS e no Safari do macOS.
 *
 * Retorna `undefined` quando a plataforma não é Apple ou quando a versão
 * atende à política — nesses casos a validação segue pelo navegador.
 */
export function validateApplePlatform(
  params: ApplePlatformParams,
): BrowserValidationResult | undefined {
  const appleVersion = getAppleVersion(params)

  if (!appleVersion) {
    return undefined
  }

  const isSupported = meetsMinimumVersion({
    policyRule: APPLE_MINIMUM_VERSION,
    version: appleVersion,
  })

  if (isSupported) {
    return undefined
  }

  const { osName, osVersion } = params
  const isMobile = isAppleMobileOS(osName)
  const systemName = isMobile ? osName : 'Safari'

  return {
    browserName: systemName,
    browserVersion: formatVersionForDisplay({ version: appleVersion }),
    isSupported: false,
    minimumBrowserName: systemName,
    minimumBrowserVersion: formatMinimumVersionForDisplay({
      policyRule: APPLE_MINIMUM_VERSION,
    }),
    osName,
    osVersion,
    updateTarget: isMobile ? 'ios' : 'mac_safari',
  }
}
